import api from '@/utils/httpClient';
import { ApiListResponse, ApiResponse } from '@oncolens/oncolens-ui/lib/dto/api/ApiResponse';
import { AxiosError } from 'axios';
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';
import {
  RiskFactorDto,
  RiskFactorForCreationDto,
  RiskFactorForUpdateDto
} from '../dtos/RiskFactor';
import { riskFactorKeys } from '../RiskFactors/riskFactor.keys';

async function getRiskFactors(episodeId: string) {
  const config: any = { containsPHI: false };
  return api.get(`/api/riskfactors?filters=episodeId==${episodeId}`, config).then((response) => {
    const parsedResponse = response.data as ApiListResponse<RiskFactorDto>;

    return parsedResponse.data;
  });
}

function useGetRiskFactors(episodeId: string, options?: UseQueryOptions<RiskFactorDto[]>) {
  return useQuery(riskFactorKeys.lists(episodeId), async () => getRiskFactors(episodeId), options);
}

export interface CreateRiskFactorParameters {
	episodeId: string;
	data: RiskFactorForCreationDto[];
}

async function createRiskFactor(episodeId: string, data: RiskFactorForCreationDto[]) {
  const config: any = { containsPHI: false };
  return api
    .post(`/api/riskfactors?episodeId=${episodeId}`, data, config)
    .then((response) => (response.data as ApiListResponse<RiskFactorDto>).data);
}

function useCreateRiskFactor(
  options?: UseMutationOptions<RiskFactorDto[], AxiosError, CreateRiskFactorParameters>
) {
  return useMutation(
    async ({ episodeId, data }: CreateRiskFactorParameters) => createRiskFactor(episodeId, data),
    options
  );
}

interface UpdateRiskFactorParameters {
	riskFactorId: string;
	data: RiskFactorForUpdateDto;
}

async function updateRiskFactor(riskFactorId: string, data: RiskFactorForUpdateDto) {
  const config: any = { containsPHI: false };
  return api
    .put(`/api/riskfactors/${riskFactorId}`, data, config)
    .then((response) => (response.data as ApiResponse<RiskFactorDto>).data);
}

function useUpdateRiskFactor(
  options?: UseMutationOptions<RiskFactorDto, AxiosError, UpdateRiskFactorParameters>
) {
  return useMutation(
    async ({ riskFactorId, data }: UpdateRiskFactorParameters) =>
      updateRiskFactor(riskFactorId, data),
    options
  );
}

async function deleteRiskFactor(riskFactorId: string) {
  const config: any = { containsPHI: false };
  return api
    .delete(`/api/riskfactors/${riskFactorId}`, config)
    .then((response) => (response.data as ApiResponse<any>).data);
}

function useDeleteRiskFactor(options?: UseMutationOptions<any, AxiosError, string>) {
  return useMutation(async (riskFactorId: string) => deleteRiskFactor(riskFactorId), options);
}

export {
  useGetRiskFactors,
  useCreateRiskFactor,
  createRiskFactor,
  useUpdateRiskFactor,
  useDeleteRiskFactor,
  getRiskFactors
};
